import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LanguageIcon from '@mui/icons-material/Language';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import DevicesIcon from '@mui/icons-material/Devices';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import TranslateIcon from '@mui/icons-material/Translate';
import SettingsIcon from '@mui/icons-material/Settings';
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import ForumIcon from '@mui/icons-material/Forum';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import config from '../config';
import LanguageSelector from './ui/LanguageSelector';
import languageData from '../config/languages.json';

// Styled components for custom design elements
const FeatureIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 'auto',
  marginRight: theme.spacing(2),
  color: theme.palette.primary.main,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(8),
  marginBottom: theme.spacing(4),
  fontWeight: 700,
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: 0,
    width: 70,
    height: 4,
    backgroundColor: theme.palette.primary.main,
  }
}));

const PricingCard = styled(Card)(({ theme, featured }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  border: featured ? `2px solid ${theme.palette.primary.main}` : 'none',
  overflow: 'visible',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
  }
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  }
}));

const CheckItem = styled(ListItem)({
  padding: '8px 0',
});

const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  padding: theme.spacing(10, 0),
  marginBottom: theme.spacing(6),
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 50%)',
    pointerEvents: 'none',
  }
}));

const FeaturedChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: -12,
  right: 24,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  boxShadow: theme.shadows[3],
}));

const FeaturesPage = () => {
  const { isAuthenticated, loginWithRedirect, user, getAccessTokenSilently } = useAuth0();
  const { isSubscriber, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [selectedLanguage, setSelectedLanguage] = useState("en-US");
  const [languagesWithTTS, setLanguagesWithTTS] = useState(new Set());
  const [isLoadingTTS, setIsLoadingTTS] = useState(false);

  // Effect to check TTS availability when a language is selected
  useEffect(() => {
    const checkTTSAvailability = async (langCode) => {
      if (languagesWithTTS.has(langCode)) return true;
      
      try {
        setIsLoadingTTS(true);
        const response = await fetch(`${config.API_URL}/api/check-tts/${langCode}`);
        
        if (!response.ok) {
          throw new Error(`API returned ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('FeaturesPage: TTS availability check:', data);
        
        if (data.has_tts) {
          setLanguagesWithTTS(prev => new Set([...prev, langCode]));
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error('FeaturesPage: Error checking TTS availability:', error);
        
        // Use fallback detection based on language code
        const commonTtsLanguages = ['en-US', 'en-GB', 'fr-FR', 'de-DE', 'it-IT', 'es-ES', 'pt-BR', 'ja-JP'];
        const baseLang = langCode.split('-')[0];
        
        // Check if this is a major language that likely has TTS support
        if (commonTtsLanguages.includes(langCode) || 
            commonTtsLanguages.some(l => l.startsWith(baseLang + '-'))) {
          console.log('FeaturesPage: Language likely has TTS support based on language code');
          setLanguagesWithTTS(prev => new Set([...prev, langCode]));
          return true;
        } else {
          console.log('FeaturesPage: Language likely does not have TTS support based on language code');
          return false;
        }
      } finally {
        setIsLoadingTTS(false);
      }
    };

    if (selectedLanguage) {
      checkTTSAvailability(selectedLanguage);
    }
  }, [selectedLanguage, languagesWithTTS]);

  // Effect to preload TTS data for common languages on component mount
  useEffect(() => {
    const preloadTTSData = async () => {
      if (languagesWithTTS.size === 0) {
        // Preload some common languages
        const commonLanguages = ["en-US", "es-ES", "fr-FR", "de-DE", "ja-JP", "zh-CN"];
        
        setIsLoadingTTS(true);
        const newLanguagesWithTTS = new Set();
        
        for (const langCode of commonLanguages) {
          try {
            const response = await fetch(`${config.API_URL}/api/check-tts/${langCode}`);
            if (response.ok) {
              const data = await response.json();
              if (data.has_tts) {
                newLanguagesWithTTS.add(langCode);
              }
            }
          } catch (error) {
            console.error(`FeaturesPage: Error checking TTS for ${langCode}:`, error);
            // If we can't check, use the fallback detection
            const commonTtsLanguages = ['en-US', 'en-GB', 'fr-FR', 'de-DE', 'it-IT', 'es-ES', 'pt-BR', 'ja-JP'];
            if (commonTtsLanguages.includes(langCode)) {
              newLanguagesWithTTS.add(langCode);
            }
          }
        }
        
        setLanguagesWithTTS(newLanguagesWithTTS);
        setIsLoadingTTS(false);
      }
    };

    preloadTTSData();
  }, []);

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const closeAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const handleStartBroadcasting = async () => {
    // Always immediately set loading state
    setIsCheckoutLoading(true);
    
    try {
      // Step 1: Check if user is logged in
      if (!isAuthenticated) {
        // Not logged in, redirect to login with return path to broadcast page
        loginWithRedirect({
          appState: { returnTo: "/broadcast" }
        });
        return;
      }

      // Step 2: If logged in, simply redirect to broadcast page and let that component
      // handle subscription checking and checkout flow if needed
      // This avoids duplicate logic and potential auth0 timing issues
      navigate('/broadcast');
      
    } catch (error) {
      console.error('Navigation error:', error);
      setAlert({
        open: true,
        message: error.message || 'An error occurred. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const faqs = [
    {
      question: "How does the translation service work?",
      answer: "Our platform captures audio in real-time, processes it through advanced speech recognition technology, translates it using neural machine translation models, and delivers it to listeners in their chosen language. The entire process happens in milliseconds, allowing for natural conversation flow."
    },
    {
      question: "What languages are supported?",
      answer: (
        <Box>
          <Typography paragraph>
            We support over 100 mainstream languages for live broadcast translation. When someone is broadcasting, listeners can select their preferred language to receive the content.
          </Typography>
          
          <Typography paragraph>
            Select a language below to see its available capabilities:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Language Selector */}
            <LanguageSelector 
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              languages={languageData.languages}
              fullWidth
            />
            
            {/* Simple Capability Display */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                mt: 1, 
                bgcolor: 'background.paper', 
                borderLeft: '4px solid', 
                borderColor: 'primary.main' 
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                {languageData.languages.find(l => l.code === selectedLanguage)?.name} Capabilities:
              </Typography>
              
              {isLoadingTTS ? (
                <Box display="flex" alignItems="center" gap={1} my={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Checking capabilities...</Typography>
                </Box>
              ) : (
                <Box>
                  <List dense disablePadding>
                    {/* Speech Recognition */}
                    <ListItem disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {selectedLanguage && languageData.languages.find(l => l.code === selectedLanguage)?.sttModel ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CheckCircleIcon color="disabled" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box component="span" sx={{ 
                            color: selectedLanguage && languageData.languages.find(l => l.code === selectedLanguage)?.sttModel ? 
                              'text.primary' : 'text.disabled' 
                          }}>
                            Speech Recognition
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ 
                            color: selectedLanguage && languageData.languages.find(l => l.code === selectedLanguage)?.sttModel ? 
                              'text.secondary' : 'text.disabled' 
                          }}>
                            {selectedLanguage && languageData.languages.find(l => l.code === selectedLanguage)?.sttModel ? 
                              "Broadcasters can speak in this language" : 
                              "Not available for broadcasters"
                            }
                          </Box>
                        }
                      />
                    </ListItem>
                    
                    {/* Written Translation */}
                    <ListItem disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Written Translation" 
                        secondary="Receive written transcriptions of broadcasts"
                      />
                    </ListItem>
                    
                    {/* Voice Synthesis */}
                    <ListItem disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {languagesWithTTS.has(selectedLanguage) ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CheckCircleIcon color="disabled" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box component="span" sx={{ 
                            color: languagesWithTTS.has(selectedLanguage) ? 
                              'text.primary' : 'text.disabled' 
                          }}>
                            Voice Synthesis
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ 
                            color: languagesWithTTS.has(selectedLanguage) ? 
                              'text.secondary' : 'text.disabled' 
                          }}>
                            {languagesWithTTS.has(selectedLanguage) ? 
                              "Listeners can hear translated audio" : 
                              "Only written translation available"
                            }
                          </Box>
                        }
                      />
                    </ListItem>
                  </List>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                    Note: All languages support written translation. The additional capabilities above determine how broadcasters and listeners can interact with the content.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      )
    },
    {
      question: "How much does it cost to broadcast?",
      answer: "Broadcasting is available for $100 per month with unlimited streaming hours and concurrent listeners. This includes access to all translation languages, technical support, and organization branding features."
    },
    {
      question: "Is there a free trial available?",
      answer: "Yes, we offer a 14-day free trial for all new subscribers. Simply create an account and subscribe to automatically receive your free trial period. No coupon code needed."
    },
    {
      question: "How many people can listen to my broadcasts?",
      answer: "There is no limit to the number of concurrent listeners. Our platform is built on scalable infrastructure that can handle thousands of simultaneous connections."
    },
    {
      question: "Do listeners need to pay or create an account?",
      answer: "No, listening is completely free and doesn't require an account. Listeners simply visit your organization's unique streaming page and select their preferred language."
    },
    {
      question: "How accurate are the translations?",
      answer: "Our translations achieve over 90% accuracy for most major languages. The system continuously improves through machine learning, and we regularly update our translation models with the latest advancements."
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 800, mb: 2 }}>
                Welcome to Glossa.link
              </Typography>
              <Typography variant="h5" component="h2" paragraph sx={{ opacity: 0.9, mb: 3 }}>
                Make the Gospel accessible in all languages through real-time, AI-powered translation for worship, sermons, and ministry.
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 4, maxWidth: '90%' }}>
                Empower your church to reach every heart. Glossa.link delivers real-time audio translation for worship services, sermons, and outreach—breaking language barriers so everyone can hear the Good News.
              </Typography>
              <Button 
                variant="contained" 
                color="secondary" 
                size="large"
                onClick={handleStartBroadcasting}
                disabled={isCheckoutLoading}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  fontWeight: 600,
                  boxShadow: '0 8px 15px rgba(0,0,0,0.2)',
                  '&:hover': {
                    boxShadow: '0 12px 20px rgba(0,0,0,0.3)',
                  }
                }}
              >
                {isCheckoutLoading ? 'Loading...' : 'Start Broadcasting Today'}
              </Button>
              <Button 
                variant="outlined" 
                color="inherit"
                size="large"
                component={Link}
                to="/stream"
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  fontWeight: 600,
                  ml: 2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  }
                }}
              >
                Try Streaming Now
              </Button>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      <Container maxWidth="lg">
        {/* Value Proposition Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Give Every Listener a Personalized Experience
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            When you broadcast with Glossa.link, your audience chooses their preferred language. 
            Our technology handles all the translation work, letting you focus on your message.
          </Typography>
        </Box>

        {/* How It Works Section */}
        <SectionTitle variant="h3" component="h2">
          How It Works
        </SectionTitle>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <RecordVoiceOverIcon color="primary" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                1. You Speak
              </Typography>
              <Typography variant="body2">
                Broadcast your audio through our simple interface with a click of a button.
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <TranslateIcon color="primary" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                2. We Translate
              </Typography>
              <Typography variant="body2">
                AI translates your audio in real-time to over 100 languages with high accuracy.
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <SettingsIcon color="primary" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                3. Listeners Choose
              </Typography>
              <Typography variant="body2">
                Listeners select their preferred language from a dropdown menu.
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <HeadsetMicIcon color="primary" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                4. They Listen
              </Typography>
              <Typography variant="body2">
                Your audience hears your content in their native language without delay.
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        {/* Use Cases Section */}
        <SectionTitle variant="h3" component="h2">
          Popular Use Cases
        </SectionTitle>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <FeatureCard elevation={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GroupIcon color="primary" sx={{ fontSize: 28, mr: 1.5 }} />
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                  Religious Services
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                Create inclusive worship experiences for diverse congregations. Sermons and services are instantly accessible to everyone regardless of language background.
              </Typography>
            </FeatureCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FeatureCard elevation={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PermPhoneMsgIcon color="primary" sx={{ fontSize: 28, mr: 1.5 }} />
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                  Conferences & Events
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                Make your events inclusive for international audiences. Speakers present in their language while attendees listen in theirs, eliminating the need for expensive interpreters.
              </Typography>
            </FeatureCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FeatureCard elevation={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GroupIcon color="primary" sx={{ fontSize: 28, mr: 1.5 }} />
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                  Educators & Classrooms
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                Support multilingual learners with live classroom translation. Teachers speak naturally while students follow along in their preferred language—no hardware or prep required.
              </Typography>
            </FeatureCard>
          </Grid>
        </Grid>

        {/* Pricing Section */}
        <SectionTitle variant="h3" component="h2" id="pricing">
          Simple, Transparent Pricing
        </SectionTitle>
        
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={6} md={5}>
            <PricingCard elevation={3}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" component="div" sx={{ color: 'text.secondary', mb: 1 }}>
                  Listener
                </Typography>
                <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 2 }}>
                  Free
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  For audience members who want to listen to broadcasts
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <List>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="Listen to any public broadcast" />
                  </CheckItem>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="250+ languages and dialects" />
                  </CheckItem>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="No account required" />
                  </CheckItem>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="Works on all modern browsers" />
                  </CheckItem>
                </List>
              </CardContent>
              <Box sx={{ mt: 'auto', p: 3, pt: 0 }}>
                <Button 
                  variant="outlined" 
                  fullWidth
                  component={Link}
                  to="/stream"
                  size="large"
                >
                  Start Listening
                </Button>
              </Box>
            </PricingCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={5}>
            <PricingCard elevation={5} featured={true}>
              <FeaturedChip label="POPULAR" />
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" component="div" sx={{ color: 'text.secondary', mb: 1 }}>
                  Broadcaster
                </Typography>
                <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 0 }}>
                  $100
                </Typography>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>per month</Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  For organizations who want to broadcast with translation
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <List>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="Unlimited broadcasting hours" />
                  </CheckItem>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="Unlimited concurrent listeners" />
                  </CheckItem>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="Broadcast in 100+ languages and dialects" />
                  </CheckItem>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="Organization branding options" />
                  </CheckItem>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="Dedicated streaming URL" />
                  </CheckItem>
                  <CheckItem>
                    <FeatureIcon>
                      <CheckCircleIcon />
                    </FeatureIcon>
                    <ListItemText primary="Priority technical support" />
                  </CheckItem>
                </List>
              </CardContent>
              <Box sx={{ mt: 'auto', p: 3, pt: 0 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleStartBroadcasting}
                >
                  Start Broadcasting
                </Button>
              </Box>
            </PricingCard>
          </Grid>
        </Grid>
        
        {/* Special Offer Section - Only shown based on auth/subscription status */}
        {/* Case 1: Not logged in - Show sign up prompt */}
        {!isAuthenticated && (
          <Box sx={{ my: 10, p: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '100%', 
                backgroundImage: 'radial-gradient(circle at 85% 15%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 50%)',
                zIndex: 0
              }} 
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 700 }}>
                Try Our Service Risk-Free
              </Typography>
              
              <Typography variant="h3" align="center" sx={{ my: 3, fontWeight: 800 }}>
                Sign Up for a 14-Day Free Trial!
              </Typography>
              
              <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                Create your free account now and subscribe to automatically get a 14-day free trial.
              </Typography>
              
              <Typography variant="body1" align="center">
                For just <strong>$100/month</strong> after your free trial, Glossa.Link gives you unlimited broadcasting—unlike competitors charging for tiered or hourly plans.
              </Typography>
              
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large"
                  onClick={() => loginWithRedirect()}
                  sx={{ 
                    px: 5, 
                    py: 1.5,
                    fontWeight: 600,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  Sign Up For Free
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Case 2: Logged in but not subscribed - Show free trial info */}
        {(isAuthenticated && !isSubscriptionLoading && !isSubscriber) && (
          <Box sx={{ my: 10, p: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '100%', 
                backgroundImage: 'radial-gradient(circle at 85% 15%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 50%)',
                zIndex: 0
              }} 
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 700 }}>
                Try Our Service Risk-Free
              </Typography>
              
              <Typography variant="h3" align="center" sx={{ my: 3, fontWeight: 800 }}>
                14-Day Free Trial on All New Subscriptions!
              </Typography>
              
              <Box sx={{ 
                maxWidth: 500, 
                mx: 'auto', 
                p: 2, 
                bgcolor: 'rgba(255,255,255,0.1)', 
                borderRadius: 1,
                border: '1px dashed rgba(255,255,255,0.5)',
                textAlign: 'center',
                mb: 4
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Just subscribe to start your free trial
                </Typography>
              </Box>
              
              <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                For just <strong>$100/month</strong> after your free trial, Glossa.Link gives you unlimited broadcasting—unlike competitors charging for tiered or hourly plans with similar features.
              </Typography>
              
              <Typography variant="body1" align="center">
                Don't miss this opportunity to transform how your organization communicates!
              </Typography>
              
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large"
                  onClick={handleStartBroadcasting}
                  disabled={isCheckoutLoading}
                  sx={{ 
                    px: 5, 
                    py: 1.5,
                    fontWeight: 600,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  {isCheckoutLoading ? 'Processing...' : 'Claim Your Free Month Now'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        
        {/* Case 3: Subscribed users - No box shown */}

        {/* FAQ Section */}
        <SectionTitle variant="h3" component="h2">
          Frequently Asked Questions
        </SectionTitle>
        
        <Box sx={{ mb: 8 }}>
          {faqs.map((faq, index) => (
            <Accordion 
              key={index} 
              expanded={expandedFaq === `panel${index}`} 
              onChange={handleFaqChange(`panel${index}`)}
              sx={{ 
                mb: 1, 
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px !important',
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  '&.Mui-expanded': { 
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  }
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        
        {/* CTA Section */}
        <Paper 
          elevation={4} 
          sx={{ 
            p: 6, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ef 100%)',
            textAlign: 'center',
            mb: 8
          }}
        >
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to broadcast to a global audience?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 700, mx: 'auto' }}>
            Start today. Begin broadcasting with real-time translations in minutes.
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            size="large"
            onClick={handleStartBroadcasting}
            disabled={isCheckoutLoading}
            sx={{ 
              px: 5, 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600 
            }}
          >
            {isCheckoutLoading ? 'Processing...' : 'Start Today'}
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 2 }}>
            Only $100/month. Cancel anytime.
          </Typography>
        </Paper>
      </Container>
      
      {/* Alert Snackbar */}
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={closeAlert}>
        <Alert onClose={closeAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeaturesPage;
